/**
 * Pure helpers for the People admin page (no React, no network) so they can be
 * unit tested in isolation.
 */
import {
  EMPTY_ADDRESS_COMPONENTS,
  addressComponentsEqual,
  composeAddress,
  hasAddressComponents,
  parseAddressComponents,
  trimAddressComponents,
} from "domain/address";

export const PERSON_TYPES = [
  { value: "UNDERWRITER", label: "Underwriter" },
  { value: "BROKER", label: "Broker" },
  { value: "PUNI_BROKER", label: "Puni Broker" },
  { value: "RETAIL_BROKER", label: "Retail Broker" },
  { value: "SURPLUS_BROKER", label: "Surplus Broker" },
  { value: "PRODUCER_BROKER", label: "Producer Broker" },
  { value: "WHOLESALE_BROKER", label: "Wholesale Broker" },
  { value: "ASSISTANT", label: "Assistant" },
];

/** Display label for a role code, falling back to the code itself. */
export const getTypeLabel = (type) => {
  const personType = PERSON_TYPES.find((t) => t.value === type);
  return personType?.label || type;
};

/**
 * Columns exported to CSV. Mirrors the table view, plus the structured address
 * components and title so the export can be cross-referenced with external
 * systems (e.g. broker office records in IMS) without re-parsing addresses.
 *
 * Each entry: [header, (person) => string]
 */
export const PEOPLE_CSV_COLUMNS = [
  ["Name", (p) => p?.name],
  ["Email", (p) => p?.email],
  ["Roles", (p) => (p?.roles || []).map(getTypeLabel).join(", ")],
  ["Company", (p) => p?.metadata?.company],
  ["Title", (p) => p?.metadata?.title],
  ["Location", (p) => p?.metadata?.location],
  ["Street", (p) => p?.metadata?.addressComponents?.street],
  ["Street 2", (p) => p?.metadata?.addressComponents?.street2],
  ["City", (p) => p?.metadata?.addressComponents?.city],
  ["State", (p) => p?.metadata?.addressComponents?.state],
  ["Zip", (p) => p?.metadata?.addressComponents?.zip],
  ["Address", (p) => p?.metadata?.address],
  ["Phone", (p) => p?.metadata?.telephone],
  ["Work", (p) => p?.metadata?.workNumber],
  // Both spellings exist in stored people configs.
  ["Mobile", (p) => p?.metadata?.mobile_number ?? p?.metadata?.mobileNumber],
  ["Licenses", (p) => (p?.metadata?.licenseNumber || []).join(", ")],
  ["Policy Underwriter", (p) => p?.metadata?.policyUnderwriter?.name],
  ["Id", (p) => p?.id],
];

/**
 * Quote a single CSV field per RFC 4180. Always quotes so embedded commas,
 * quotes and newlines (multi-line addresses) survive a round trip into Excel.
 */
export const csvEscape = (value) => {
  if (value === undefined || value === null) {
    return '""';
  }
  return `"${String(value).replace(/"/g, '""')}"`;
};

/**
 * Build the CSV text for a list of people.
 *
 * @param {Array<Object>} people - People as returned by list-people
 * @returns {string} CSV text with a UTF-8 BOM and CRLF line endings (Excel-friendly)
 */
export const buildPeopleCsv = (people) => {
  const header = PEOPLE_CSV_COLUMNS.map(([name]) => csvEscape(name));
  const rows = (people || []).map((person) =>
    PEOPLE_CSV_COLUMNS.map(([, getValue]) => csvEscape(getValue(person)))
  );
  return (
    "﻿" + [header, ...rows].map((row) => row.join(",")).join("\r\n") + "\r\n"
  );
};

/**
 * File name for a people export, e.g. `people-all-2026-09-09.csv` or
 * `people-broker-2026-09-09.csv` when a role filter is active.
 */
export const peopleCsvFilename = (roleFilter = "ALL", date = new Date()) => {
  const role = (roleFilter || "ALL").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const day = date.toISOString().slice(0, 10);
  return `people-${role}-${day}.csv`;
};

/**
 * Initial structured-address values for the person editor.
 *
 * Stored components win. Otherwise a legacy flat `metadata.address` is parsed into
 * components with the same heuristic the insured-address input uses (#7541), so the
 * editor shows the address broken out rather than dumping the whole string in line 1.
 */
export const seedAddressComponents = (person) => {
  const stored = person?.metadata?.addressComponents;
  if (stored) {
    return { ...EMPTY_ADDRESS_COMPONENTS, ...stored };
  }
  return parseAddressComponents(person?.metadata?.address || "");
};

/**
 * The `{ address, addressComponents }` half of a create/update-person payload.
 *
 * The flat `metadata.address` string is consumed by the bff `set_broker` call and by
 * document templates, so it must not change format just because a person was opened and
 * saved. `parseAddressComponents` is a heuristic and `composeAddress` re-joins on
 * newlines, so a parse/compose round trip is lossy: re-composing an untouched seed would
 * silently rewrite every stored address the first time each person is saved.
 *
 * Therefore: if the components in the form are unchanged from the seed, the original flat
 * string and the original stored components are returned verbatim. The flat address is
 * only recomposed when the user actually edits a component — and then it is composed the
 * same way the backend `compose_address` composes it under `virtualize_address`, so the
 * two agree.
 */
export const buildPersonAddressUpdate = (person, formComponents) => {
  const originalAddress = person?.metadata?.address || "";
  const storedComponents = person?.metadata?.addressComponents || null;
  const trimmed = trimAddressComponents(formComponents);

  if (addressComponentsEqual(trimmed, seedAddressComponents(person))) {
    return {
      address: originalAddress.trim() || null,
      addressComponents: storedComponents,
    };
  }

  // Empty values, not null, so emptying every field really clears: `update_person` skips
  // `None` ("not sent") but writes "" and {}; `create_person` ignores both.
  return {
    address: composeAddress(trimmed),
    addressComponents: hasAddressComponents(trimmed) ? trimmed : {},
  };
};
